import { useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import { RESEARCH_POWER } from '../../config/data';
import { t, isOverseas } from '../../utils/i18n';
import ChinaMap from './ChinaMap';
import powerArchImg from '../../assets/images/power-arch-diagram.png';
import powerArchImgEn from '../../assets/images/power-arch-diagram-en.png';
import './index.css';

const SOLUTION_BUTTONS = [
    { id: 'power-card-0', label: t('风光电站功率预测与交易', 'Power Forecasting & Trading for Renewable Plants') },
    { id: 'power-card-1', label: t('储能系统智慧运营', 'Smart Energy Storage Operations') },
    { id: 'power-card-2', label: t('用户侧柔性负荷运行管理', 'Customer-Side Flexible Load Management') },
    { id: 'power-card-3', label: t('用户侧电力零售与交易', 'Customer-Side Electricity Retail & Trading') },
];

export default function PowerIntelligencePage() {
    useEffect(() => {
        document.title = t('数智电力 · 至知创新研究院', 'Power Intelligence · IQuest Research');
    }, []);

    const scrollToCard = useCallback((cardId) => {
        const el = document.getElementById(cardId);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, []);

    return (
        <div className="page-shell power-page-shell">
            <PageHeader
                eyebrow={t('数智电力 · Power Intelligence', 'Power Intelligence')}
                title={t(
                    '数智电力 —— 用 AI 激发电力资源潜能',
                    <>Power Intelligence — Unlocking Electric<br/>Resource Potential with AI</>
                )}
                subtitle={t(
                    '聚焦 AI 在新型电力系统中的核心应用，面向风光新能源、储能、柔性负荷与电力交易等场景，构建从数据感知、时序预测、市场建模到交易决策和风险复盘的全链路技术体系。',
                    'Focusing on AI-driven next-generation power systems, this research direction targets wind and solar renewables, energy storage, flexible loads, and electric markets, building an end-to-end stack from data sensing and time-series forecasting to market modeling, trading decisions, and risk review.'
                )}
            />

            {/* 架构图区 */}
            <section className="section power-arch-section">
                <div className="container">
                    <h2 className="power-arch-title">
                        {t('从数据到决策，全栈式 AI 能源管理平台', 'From Data to Decision: A Full-Stack AI Energy Management Platform')}
                    </h2>
                    <div className="power-arch-diagram">
                        <img src={isOverseas ? powerArchImgEn : powerArchImg} alt={t('架构图', 'Architecture Diagram')} className="arch-img" />
                        {/* 解决方案按钮覆盖层 */}
                        <div className="arch-solution-overlay">
                            {SOLUTION_BUTTONS.map((btn) => (
                                <button
                                    key={btn.id}
                                    className="arch-solution-btn"
                                    onClick={() => scrollToCard(btn.id)}
                                    title={btn.label}
                                >
                                    {btn.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* 产品卡片 2x2 */}
            <section className="section power-cards-section">
                <div className="container">
                    <div className="power-cards-grid">
                        {RESEARCH_POWER.map((item, idx) => (
                            <article className="power-card" key={idx} id={`power-card-${idx}`}>
                                <div className="power-card-img">
                                    {item.slogan && (
                                        <div className="power-card-slogan">{item.slogan}</div>
                                    )}
                                    <img src={item.img} alt={item.title} loading="lazy" decoding="async" />
                                </div>
                                <div className="power-card-body">
                                    <h3 className="power-card-title">{item.title}</h3>
                                    <p className="power-card-desc">{item.desc}</p>
                                    {item.tags && item.tags.length > 0 && (
                                        <div className="power-card-tags">
                                            {item.tags.map((tag) => (
                                                <span className="power-card-tag" key={tag}>{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* 落地区域地图 */}
            <section className="section power-map-section">
                <div className="container">
                    <div className="power-map-legend">
                        <span className="power-map-legend-item legend-active">
                            <span className="legend-dot" />{t('已落地运营', 'Operational Deployments')}
                        </span>
                        <span className="power-map-legend-item legend-verified">
                            <span className="legend-dot" />{t('已核验/公示中', 'Under Verification & Public Notice')}
                        </span>
                        <span className="power-map-legend-item legend-planned">
                            <span className="legend-dot" />{t('近期规划', 'Upcoming Rollout Plan')}
                        </span>
                    </div>
                    <ChinaMap />
                </div>
            </section>
        </div>
    );
}
